import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { 
  ArrowLeft, Calendar, Download, Users, UserPlus, 
  TrendUp, Star, Clock, ChartLineUp
} from '@phosphor-icons/react';
import { maskPhone } from '../utils/maskPhone';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminActivityDashboard = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('dws_user') || localStorage.getItem('user') || '{}');
  const isHandler = user.role !== 'super_admin';
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(() => readInstantCache('admin_activity', 'all') || {
    registered_today: 0,
    active_today: 0,
    moved_today: 0,
    total_logins: 0
  });
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [movedClientsToday, setMovedClientsToday] = useState([]);
  const [loginSessions, setLoginSessions] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(() => !readInstantCache('admin_activity', 'all'));
  const [exportRange, setExportRange] = useState('today');
  const [selectedView, setSelectedView] = useState(null); // 'registered', 'active', 'moved', 'logins'
  const [expandedUsers, setExpandedUsers] = useState({}); // Track expanded user sessions
  const [showDatePicker, setShowDatePicker] = useState(false); // Calendar modal
  
  // Toast notification
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Calculate min date (6 months ago)
  const getMinDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };
  
  // Calculate max date (today)
  const getMaxDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Fetch data
  useEffect(() => {
    fetchActivityData();
    fetchTopClients();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchActivityData, 10000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchActivityData = async () => {
    try {
      if (!readInstantCache('admin_activity', 'all')) setLoading(true);
      
      // Fetch summary
      const summaryRes = await axios.get(`${API}/admin/activity/summary?date=${selectedDate}`);
      setSummary(summaryRes.data);
      writeInstantCache('admin_activity', 'all', summaryRes.data);
      
      // Fetch registered users
      const registeredRes = await axios.get(`${API}/admin/activity/registered?date=${selectedDate}`);
      setRegisteredUsers(registeredRes.data);
      
      // Fetch active users
      const activeRes = await axios.get(`${API}/admin/activity/active?date=${selectedDate}`);
      setActiveUsers(activeRes.data);
      
      // Fetch moved clients (filter by today's date)
      const movedRes = await axios.get(`${API}/clients/moved`, {
        params: {
          user_id: user.id || '9f2f87ad-a98c-432f-a813-2eb385198afc',
          role: user.role || 'super_admin'
        }
      });
      
      // Filter to only today's moved clients with robust date matching
      const todayStr = selectedDate; // Format: "2026-04-07"
      const todayMoved = (movedRes.data || []).filter(c => {
        if (!c.created_at) return false;
        // Extract date part from ISO string (handles both "2026-04-07" and "2026-04-07T...")
        const createdDate = c.created_at.split('T')[0];
        return createdDate === todayStr;
      });
      
      console.log('📊 Moved clients debug:', {
        total: movedRes.data?.length || 0,
        todayFiltered: todayMoved.length,
        selectedDate: todayStr
      });
      
      setMovedClientsToday(todayMoved);
      
      // Fetch login sessions (same as active users)
      console.log('🔑 Login sessions debug:', {
        sessions: activeRes.data?.length || 0,
        selectedDate: selectedDate
      });
      
      setLoginSessions(activeRes.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch activity data:', error);
      setLoading(false);
    }
  };

  const fetchTopClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients/top`);
      setTopClients(res.data);
    } catch (error) {
      console.error('Failed to fetch top clients:', error);
    }
  };

  const addToTopClients = async (customerId) => {
    try {
      await axios.post(`${API}/admin/clients/add-to-top`, {
        customer_id: customerId,
        admin_id: user.id
      });
      fetchTopClients();
      showToast('✅ Added to Top Clients!', 'success');
    } catch (error) {
      console.error('Failed to add to top clients:', error);
      showToast('❌ Failed to add', 'error');
    }
  };

  const removeFromTopClients = async (customerId) => {
    try {
      await axios.post(`${API}/admin/clients/remove-from-top/${customerId}`);
      fetchTopClients();
      showToast('✅ Removed from Top Clients!', 'success');
    } catch (error) {
      console.error('Failed to remove from top clients:', error);
      showToast('❌ Failed to remove', 'error');
    }
  };
  
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleExport = async (format) => {
    try {
      // Calculate date range based on selection
      let startDate, endDate;
      const today = new Date();
      
      switch(exportRange) {
        case 'today':
          startDate = endDate = today.toISOString().split('T')[0];
          break;
        case 'week':
          startDate = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
          endDate = new Date().toISOString().split('T')[0];
          break;
        case 'month':
          startDate = new Date(today.setMonth(today.getMonth() - 1)).toISOString().split('T')[0];
          endDate = new Date().toISOString().split('T')[0];
          break;
        case '3months':
          startDate = new Date(today.setMonth(today.getMonth() - 3)).toISOString().split('T')[0];
          endDate = new Date().toISOString().split('T')[0];
          break;
        case '6months':
          startDate = new Date(today.setMonth(today.getMonth() - 6)).toISOString().split('T')[0];
          endDate = new Date().toISOString().split('T')[0];
          break;
        default:
          startDate = endDate = selectedDate;
      }

      const response = await axios.post(`${API}/admin/activity/export`, {
        format,
        start_date: startDate,
        end_date: endDate,
        include_sections: true
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `activity_report_${startDate}_to_${endDate}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert(`✅ ${format.toUpperCase()} exported successfully!`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('❌ Export failed');
    }
  };

  const isTopClient = (customerId) => {
    return topClients.some(tc => tc.customer_id === customerId);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50" style={isHandler ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}}>
      {/* Header */}
      <div className="bg-white border-b flex-shrink-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Client Activity Dashboard</h1>
                <p className="text-sm text-gray-500">Real-time tracking & analytics</p>
              </div>
            </div>
            
            {/* Live indicator */}
            <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-green-700">Live</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* Date Selector - Improved with 6 months range */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Calendar size={20} className="text-gray-600" />
              <input
                type="date"
                value={selectedDate}
                min={getMinDate()}
                max={getMaxDate()}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-600">
                {new Date(selectedDate).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <button
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                className="px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Today
              </button>
            </div>
            
            {/* Export Section */}
            <div className="flex items-center gap-3">
              <select
                value={exportRange}
                onChange={(e) => setExportRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last Month</option>
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
              </select>
              
              {!isHandler && (
              <button
                onClick={() => handleExport('pdf')}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                <Download size={18} />
                PDF
              </button>
              )}
              
              {!isHandler && (
              <button
                onClick={() => handleExport('excel')}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <Download size={18} />
                Excel
              </button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            onClick={() => setSelectedView(selectedView === 'registered' ? null : 'registered')}
            className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-6 text-white cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${selectedView === 'registered' ? 'ring-4 ring-blue-300' : ''}`}
          >
            <div className="flex items-center justify-between mb-2">
              <UserPlus size={24} className="opacity-80" />
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full">New</span>
            </div>
            <div className="text-3xl font-bold">{summary.registered_today}</div>
            <div className="text-sm opacity-90">
              {selectedDate === new Date().toISOString().split('T')[0] 
                ? 'Registered Today' 
                : `Registered on ${new Date(selectedDate).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' })}`
              }
            </div>
            <div className="text-xs mt-2 opacity-75">👆 Click to view list</div>
          </div>

          <div 
            onClick={() => setSelectedView(selectedView === 'active' ? null : 'active')}
            className={`bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${selectedView === 'active' ? 'ring-4 ring-green-300' : ''}`}
          >
            <div className="flex items-center justify-between mb-2">
              <ChartLineUp size={24} className="opacity-80" />
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Active</span>
            </div>
            <div className="text-3xl font-bold">{summary.active_today}</div>
            <div className="text-sm opacity-90">
              {selectedDate === new Date().toISOString().split('T')[0] 
                ? 'Active Today' 
                : `Active on ${new Date(selectedDate).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' })}`
              }
            </div>
            <div className="text-xs mt-2 opacity-75">👆 Click to view list</div>
          </div>

          <div 
            onClick={() => setSelectedView(selectedView === 'logins' ? null : 'logins')}
            className={`bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md p-6 text-white cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${selectedView === 'logins' ? 'ring-4 ring-orange-300' : ''}`}
          >
            <div className="flex items-center justify-between mb-2">
              <Clock size={24} className="opacity-80" />
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Total</span>
            </div>
            <div className="text-3xl font-bold">{summary.total_logins}</div>
            <div className="text-sm opacity-90">
              {selectedDate === new Date().toISOString().split('T')[0] 
                ? 'Total Logins Today' 
                : `Logins on ${new Date(selectedDate).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' })}`
              }
            </div>
            <div className="text-xs mt-2 opacity-75">👆 Click to view sessions</div>
          </div>
        </div>

        {/* Selected View Detail Section */}
        {selectedView && (
          <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-blue-300 animate-fade-in">
            {selectedView === 'registered' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">
                    📋 Registered Today Details
                    <span className="ml-2 bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                      {registeredUsers.length}
                    </span>
                  </h2>
                  <button 
                    onClick={() => setSelectedView(null)}
                    className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ✕ Close
                  </button>
                </div>

                {registeredUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No new registrations today</div>
                ) : (
                  <div className="space-y-2">
                    {registeredUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {user.username?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">{user.username}</div>
                            <div className="text-sm text-gray-600">{maskPhone(user.phone, currentUser)}</div>
                          </div>
                          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                            NEW
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">{formatTime(user.created_at)}</span>
                          {!isTopClient(user.id) && (
                            <button
                              onClick={() => addToTopClients(user.id)}
                              className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
                            >
                              <Star size={16} />
                              Add to Top
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {selectedView === 'active' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">
                    📊 Active Today Details
                    <span className="ml-2 bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
                      {activeUsers.length} unique users
                    </span>
                  </h2>
                  <button 
                    onClick={() => setSelectedView(null)}
                    className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ✕ Close
                  </button>
                </div>

                {activeUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No active users today</div>
                ) : (
                  <div className="space-y-3">
                    {activeUsers.map((user) => (
                      <div key={user.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                        {/* Main User Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.username?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-semibold text-gray-800">{user.username}</div>
                                {user.is_active && (
                                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                                    ONLINE
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">{maskPhone(user.phone, currentUser)}</div>
                              {/* Login Count Badge */}
                              {user.login_count > 1 && (
                                <div className="mt-1">
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                                    🔑 Logged in {user.login_count} times today
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <div className="text-sm font-semibold text-gray-800">
                                {formatDuration(user.duration_seconds)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Last: {formatTime(user.last_seen)}
                              </div>
                            </div>
                            {/* Expand/Collapse Button */}
                            {user.login_count > 1 && (
                              <button
                                onClick={() => toggleUserExpand(user.id)}
                                className="px-3 py-1 bg-green-200 hover:bg-green-300 text-green-800 text-xs rounded-lg transition-colors font-medium"
                              >
                                {expandedUsers[user.id] ? '▲ Hide' : '▼ Show All'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Latest Session Timeline */}
                        <div className="mt-3 pt-3 border-t border-green-200 text-sm text-gray-700">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Latest Session:</span>
                            <div className="flex items-center gap-2">
                              <span>Login: {formatTime(user.login_time)}</span>
                              <span>•</span>
                              <span>{user.logout_time ? `Logout: ${formatTime(user.logout_time)}` : 'Still Active'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Sessions List */}
                        {expandedUsers[user.id] && user.login_sessions && user.login_sessions.length > 1 && (
                          <div className="mt-3 pt-3 border-t border-green-300">
                            <div className="text-xs font-semibold text-gray-700 mb-2">All Login Sessions Today:</div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {user.login_sessions.map((session, idx) => (
                                <div key={idx} className="bg-white/60 p-3 rounded border border-green-200 text-xs">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-green-600 text-white px-2 py-0.5 rounded font-medium">#{idx + 1}</span>
                                      <span className="text-gray-700">
                                        Login: {formatTime(session.login_time)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                      <span>Duration: {formatDuration(session.duration_seconds)}</span>
                                      <span>•</span>
                                      <span>{session.logout_time ? `Logout: ${formatTime(session.logout_time)}` : '🟢 Active'}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {selectedView === 'logins' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">
                    🔑 Login Sessions Today
                    <span className="ml-2 bg-orange-100 text-orange-800 text-sm px-2 py-1 rounded-full">
                      {loginSessions.length} unique users
                    </span>
                  </h2>
                  <button 
                    onClick={() => setSelectedView(null)}
                    className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ✕ Close
                  </button>
                </div>

                {loginSessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock size={48} className="mx-auto mb-2 opacity-30" />
                    <p>No login sessions today</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {loginSessions.map((user) => (
                      <div key={user.id} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        {/* Main User Row */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.username?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-semibold text-gray-800">{user.username}</div>
                                {user.is_active && (
                                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                                    ONLINE
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">{maskPhone(user.phone, currentUser)}</div>
                              {/* Login Count Badge */}
                              {user.login_count > 1 && (
                                <div className="mt-1">
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                                    🔑 {user.login_count} logins today
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div className="text-sm font-semibold text-gray-800">
                              {formatDuration(user.duration_seconds)}
                            </div>
                            {/* Expand/Collapse Button */}
                            {user.login_count > 1 && (
                              <button
                                onClick={() => toggleUserExpand(user.id)}
                                className="px-3 py-1 bg-orange-200 hover:bg-orange-300 text-orange-800 text-xs rounded-lg transition-colors font-medium"
                              >
                                {expandedUsers[user.id] ? '▲ Hide' : '▼ Show All'}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Latest Session Timeline */}
                        <div className="mt-2 pt-2 border-t border-orange-200 text-xs text-gray-600">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Latest:</span>
                            <div className="flex items-center gap-2">
                              <span>Login: {formatTime(user.login_time)}</span>
                              <span>•</span>
                              <span>{user.logout_time ? `Logout: ${formatTime(user.logout_time)}` : 'Still Active'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Sessions List */}
                        {expandedUsers[user.id] && user.login_sessions && user.login_sessions.length > 1 && (
                          <div className="mt-3 pt-3 border-t border-orange-300">
                            <div className="text-xs font-semibold text-gray-700 mb-2">All Login Sessions:</div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {user.login_sessions.map((session, idx) => (
                                <div key={idx} className="bg-white/60 p-3 rounded border border-orange-200 text-xs">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-orange-600 text-white px-2 py-0.5 rounded font-medium">#{idx + 1}</span>
                                      <span className="text-gray-700">
                                        {formatTime(session.login_time)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                      <span>{formatDuration(session.duration_seconds)}</span>
                                      <span>•</span>
                                      <span>{session.logout_time ? formatTime(session.logout_time) : '🟢 Active'}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Top Clients Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star size={24} weight="fill" className="text-yellow-500" />
              <h2 className="text-xl font-bold text-gray-800">Top Clients</h2>
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                {topClients.length}
              </span>
            </div>
          </div>

          {topClients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Star size={48} className="mx-auto mb-2 opacity-30" />
              <p>No top clients yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {topClients.map((tc) => (
                <div key={tc.id} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Star size={20} weight="fill" className="text-yellow-500" />
                    <div>
                      <div className="font-semibold text-gray-800">{tc.customer?.username}</div>
                      <div className="text-sm text-gray-600">{maskPhone(tc.customer?.phone, currentUser)}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromTopClients(tc.customer_id)}
                    className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`px-6 py-4 rounded-lg shadow-xl ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white flex items-center gap-3 animate-slide-in`}>
            <span className="text-xl">{toast.type === 'success' ? '✓' : '✕'}</span>
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading activity data...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivityDashboard;
